// src/app/modules/Recrutement/Responsable RH/Ressources JS/Etablissements/Etablissements.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    X,
    Trash2,
    Eye,
    Edit2,
    Search,
    Upload,
    Building2,
    Image as ImageIcon,
} from "lucide-react";

import "./Etablissements.css";

import {
    fetchEtablissementsRequest,
    createEtablissementRequest,
    updateEtablissementRequest,
    deleteEtablissementRequest,
} from "../../../redux/actions/etablissementsActions";

import { API_BASE_URL } from "../../../../api";

const Modal = ({ open, title, children, footer, onClose, size = "md" }) => {
    if (!open) return null;
    const sizeClass = size === "lg" ? "etbModal__panel--lg" : "";
    return (
        <div className="etbModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`etbModal__panel ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="etbModal__header">
                    <div className="etbModal__title">{title}</div>
                    <button className="etbIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>
                <div className="etbModal__body">{children}</div>
                {footer && <div className="etbModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

export default function Etablissements() {
    const dispatch = useDispatch();
    const { etablissements, loading } = useSelector((s) => s.etablissements);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [selectedEtablissement, setSelectedEtablissement] = useState(null);

    const [form, setForm] = useState({
        code: "",
        nom: "",
        abreviation: "",
        email: "",
        description: "",
        imageFile: null,
        imagePreview: null,
    });

    const [filters, setFilters] = useState({
        code: "",
        nom: "",
        abreviation: "",
    });

    useEffect(() => {
        dispatch(fetchEtablissementsRequest());
    }, [dispatch]);

    const filteredEtablissements = useMemo(() => {
        let data = [...(etablissements || [])];

        // masquer l'établissement "Autre" même s'il existe en BDD
        data = data.filter((e) => {
            const nom = String(e.nom || "").trim().toLowerCase();
            const abreviation = String(e.abreviation || "").trim().toLowerCase();
            const code = String(e.code || "").trim().toLowerCase();

            return nom !== "autre" && abreviation !== "autre" && code !== "autre";
        });

        if (filters.code) {
            data = data.filter((e) => (e.code || "").toLowerCase().includes(filters.code.toLowerCase()));
        }
        if (filters.nom) {
            data = data.filter((e) => (e.nom || "").toLowerCase().includes(filters.nom.toLowerCase()));
        }
        if (filters.abreviation) {
            data = data.filter((e) => (e.abreviation || "").toLowerCase().includes(filters.abreviation.toLowerCase()));
        }
        return data;
    }, [etablissements, filters]);

    const resetForm = () => {
        setForm({
            code: "",
            nom: "",
            abreviation: "",
            email: "",
            description: "",
            imageFile: null,
            imagePreview: null,
        });
    };

    const openCreate = () => {
        resetForm();
        setShowAddModal(true);
    };

    const closeCreate = () => {
        setShowAddModal(false);
        resetForm();
    };

    const openEdit = (etb) => {
        setSelectedEtablissement(etb);
        setForm({
            code: etb?.code || "",
            nom: etb?.nom || "",
            abreviation: etb?.abreviation || "",
            email: etb?.email || "",
            description: etb?.description || "",
            imageFile: null,
            imagePreview: null,
        });
        setShowEditModal(true);
    };

    const closeEdit = () => {
        setShowEditModal(false);
        setSelectedEtablissement(null);
        resetForm();
    };

    const openView = (etb) => {
        setSelectedEtablissement(etb);
        setShowViewModal(true);
    };

    const closeView = () => {
        setShowViewModal(false);
        setSelectedEtablissement(null);
    };

    const openDelete = (etb) => {
        setSelectedEtablissement(etb);
        setShowDeleteModal(true);
    };

    const closeDelete = () => {
        setShowDeleteModal(false);
        setSelectedEtablissement(null);
    };

    const handleFilterChange = (field, value) => setFilters((p) => ({ ...p, [field]: value }));

    const resetFilters = () => {
        setFilters({ code: "", nom: "", abreviation: "" });
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setForm((prev) => ({
                ...prev,
                imageFile: file,
                imagePreview: reader.result,
            }));
        };
        reader.readAsDataURL(file);
    };

    const isAnyFilterFilled = Object.values(filters).some((val) => val !== "");

    const validateForm = () => {
        if (!form.code?.trim()) {
            alert("Code obligatoire");
            return false;
        }
        if (!form.nom?.trim()) {
            alert("Nom obligatoire");
            return false;
        }
        if (!form.email?.trim()) {
            alert("Email obligatoire");
            return false;
        }
        return true;
    };

    const submitCreate = () => {
        if (!validateForm()) return;

        dispatch(
            createEtablissementRequest({
                code: form.code,
                nom: form.nom,
                abreviation: form.abreviation,
                email: form.email,
                description: form.description,
                imageFile: form.imageFile,
            })
        );

        closeCreate();
    };

    const submitEdit = () => {
        if (!validateForm()) return;

        dispatch(
            updateEtablissementRequest(selectedEtablissement.id, {
                code: form.code,
                nom: form.nom,
                abreviation: form.abreviation,
                email: form.email,
                description: form.description,
                imageFile: form.imageFile,
            })
        );

        closeEdit();
    };

    const confirmDelete = () => {
        dispatch(deleteEtablissementRequest(selectedEtablissement?.id));
        closeDelete();
    };

    // ✅ IMPORTANT: images doivent utiliser API_BASE_URL (sinon React essaie sur :3000)
    const getImageUrl = (picture) => `${API_BASE_URL}/api/etablissements/images/${picture}`;

    // scroll lock modals
    useEffect(() => {
        const isAnyModalOpen = showAddModal || showEditModal || showViewModal || showDeleteModal;
        document.body.style.overflow = isAnyModalOpen ? "hidden" : "unset";
        return () => (document.body.style.overflow = "unset");
    }, [showAddModal, showEditModal, showViewModal, showDeleteModal]);

    return (
        <div className="etbPage">
            <div className="etbHeader">
                <div>
                    <h1>Établissements</h1>
                    <p>Gérez la liste des établissements d'enseignement</p>
                </div>
            </div>

            {/* Filtres */}
            <div className="etbWrap">
                <div className="etbCard">
                    <div className="etbBody">
                        <div className="etbFilterSection">
                            <div className="etbFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="etbFilterGrid">
                                <div className="etbField">
                                    <label>Code</label>
                                    <input
                                        type="text"
                                        value={filters.code}
                                        onChange={(e) => handleFilterChange("code", e.target.value)}
                                        placeholder="Rechercher par code..."
                                    />
                                </div>

                                <div className="etbField">
                                    <label>Nom</label>
                                    <input
                                        type="text"
                                        value={filters.nom}
                                        onChange={(e) => handleFilterChange("nom", e.target.value)}
                                        placeholder="Rechercher par nom..."
                                    />
                                </div>

                                <div className="etbField">
                                    <label>Abréviation</label>
                                    <input
                                        type="text"
                                        value={filters.abreviation}
                                        onChange={(e) => handleFilterChange("abreviation", e.target.value)}
                                        placeholder="Rechercher par abréviation..."
                                    />
                                </div>
                            </div>

                            <div className="etbFilterActions">
                                <button className="etbBtn etbBtn--primary" type="button" onClick={openCreate} disabled={loading}>
                                    <Plus size={16} />
                                    Ajouter un établissement
                                </button>

                                <button
                                    className={`etbBtn ${isAnyFilterFilled ? "etbBtn--warning" : "etbBtn--ghost"}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Liste */}
            <div className="etbWrap">
                <div className="etbCard">
                    <div className="etbBody">
                        <div className="etbTopRow">
                            <h2 className="etbTitle">Établissements ({filteredEtablissements.length})</h2>
                        </div>

                        <div className="etbTableWrap">
                            <table className="etbTable">
                                <thead>
                                <tr>
                                    <th>Logo</th>
                                    <th>Code</th>
                                    <th>Nom</th>
                                    <th>Abréviation</th>
                                    <th>Email</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {filteredEtablissements.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="etbEmptyCell">
                                            Aucun établissement trouvé
                                        </td>
                                    </tr>
                                ) : (
                                    filteredEtablissements.map((etb) => (
                                        <tr key={etb.id}>
                                            <td>
                                                {etb.picture ? (
                                                    <div className="etbLogoBox">
                                                        <img
                                                            src={getImageUrl(etb.picture)}
                                                            alt="logo"
                                                            className="etbLogoImg"
                                                            onError={(e) => {
                                                                // fallback si image introuvable
                                                                e.currentTarget.style.display = "none";
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="etbLogoPlaceholder">
                                                        <ImageIcon size={18} />
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                <span className="etbStrong">{etb.code}</span>
                                            </td>

                                            <td>
                          <span className="etbInline">
                            <Building2 size={14} />
                            <span className="etbStrong">{etb.nom}</span>
                          </span>
                                            </td>

                                            <td className="etbMuted">{etb.abreviation || "—"}</td>
                                            <td className="etbMuted">{etb.email || "—"}</td>

                                            <td className="is-right">
                                                <div className="etbRowActions">
                                                    <button className="etbIconBtn" type="button" title="Voir" onClick={() => openView(etb)}>
                                                        <Eye size={16} />
                                                    </button>
                                                    <button className="etbIconBtn" type="button" title="Modifier" onClick={() => openEdit(etb)}>
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className="etbIconBtn etbIconBtn--danger"
                                                        type="button"
                                                        title="Supprimer"
                                                        onClick={() => openDelete(etb)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>

                        {/* petit hint chargement */}
                        {loading && <div style={{ paddingTop: 10, opacity: 0.7 }}>Chargement...</div>}
                    </div>
                </div>
            </div>

            {/* Modal Ajout */}
            <Modal
                open={showAddModal}
                title="Nouvel établissement"
                onClose={closeCreate}
                size="lg"
                footer={
                    <>
                        <button className="etbBtn etbBtn--ghost" type="button" onClick={closeCreate}>
                            Annuler
                        </button>
                        <button className="etbBtn etbBtn--primary" type="button" onClick={submitCreate}>
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className="etbForm">
                    <div className="etbFormRow">
                        <div className="etbField">
                            <label>Code *</label>
                            <input
                                type="text"
                                value={form.code}
                                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                                placeholder="Ex: ENSA"
                            />
                        </div>

                        <div className="etbField">
                            <label>Nom *</label>
                            <input
                                type="text"
                                value={form.nom}
                                onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                                placeholder="Nom complet de l'établissement"
                            />
                        </div>

                        <div className="etbField">
                            <label>Abréviation</label>
                            <input
                                type="text"
                                value={form.abreviation}
                                onChange={(e) => setForm((p) => ({ ...p, abreviation: e.target.value }))}
                                placeholder="Ex: ENSIAS"
                            />
                        </div>
                    </div>

                    <div className="etbFormRow">
                        <div className="etbField">
                            <label>Email *</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                                placeholder="contact@etablissement.ma"
                            />
                        </div>

                        <div className="etbField">
                            <label>Description</label>
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                placeholder="Description de l'établissement..."
                            />
                        </div>
                    </div>

                    <div className="etbField">
                        <label>Image/Logo</label>
                        <div className="etbFileUpload">
                            <input type="file" id="image-upload" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                            <label htmlFor="image-upload" className="etbFileUploadBtn">
                                <Upload size={16} />
                                Choisir une image
                            </label>

                            {form.imagePreview && (
                                <div className="etbImagePreview">
                                    <img src={form.imagePreview} alt="Aperçu" />
                                    <button
                                        type="button"
                                        className="etbRemoveImage"
                                        onClick={() => setForm((p) => ({ ...p, imageFile: null, imagePreview: null }))}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {form.imageFile && <div className="etbFileName">{form.imageFile.name}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Modification */}
            <Modal
                open={showEditModal}
                title={`Modifier — ${selectedEtablissement?.nom || ""}`}
                onClose={closeEdit}
                size="lg"
                footer={
                    <>
                        <button className="etbBtn etbBtn--ghost" type="button" onClick={closeEdit}>
                            Annuler
                        </button>
                        <button className="etbBtn etbBtn--primary" type="button" onClick={submitEdit}>
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className="etbForm">
                    <div className="etbFormRow">
                        <div className="etbField">
                            <label>Code *</label>
                            <input type="text" value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
                        </div>

                        <div className="etbField">
                            <label>Nom *</label>
                            <input type="text" value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))} />
                        </div>

                        <div className="etbField">
                            <label>Abréviation</label>
                            <input
                                type="text"
                                value={form.abreviation}
                                onChange={(e) => setForm((p) => ({ ...p, abreviation: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="etbFormRow">
                        <div className="etbField">
                            <label>Email *</label>
                            <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                        </div>

                        <div className="etbField">
                            <label>Description</label>
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            />
                        </div>
                    </div>

                    {selectedEtablissement?.picture && (
                        <div className="etbExistingImage">
                            <ImageIcon size={14} />
                            <span>Image actuelle: {selectedEtablissement.picture}</span>
                        </div>
                    )}

                    <div className="etbField">
                        <label>Image/Logo (remplacer)</label>
                        <div className="etbFileUpload">
                            <input type="file" id="image-upload-edit" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                            <label htmlFor="image-upload-edit" className="etbFileUploadBtn">
                                <Upload size={16} />
                                {selectedEtablissement?.picture ? "Remplacer l'image" : "Choisir une image"}
                            </label>

                            {form.imagePreview && (
                                <div className="etbImagePreview">
                                    <img src={form.imagePreview} alt="Aperçu" />
                                    <button
                                        type="button"
                                        className="etbRemoveImage"
                                        onClick={() => setForm((p) => ({ ...p, imageFile: null, imagePreview: null }))}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {form.imageFile && <div className="etbFileName">{form.imageFile.name}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal Aperçu */}
            <Modal
                open={showViewModal}
                title={`Détails — ${selectedEtablissement?.nom || ""}`}
                onClose={closeView}
                footer={
                    <button className="etbBtn etbBtn--primary" type="button" onClick={closeView}>
                        Fermer
                    </button>
                }
            >
                {selectedEtablissement && (
                    <div className="etbViewContent">
                        <div className="etbViewRow">
                            <div className="etbViewLabel">Code:</div>
                            <div className="etbViewValue">{selectedEtablissement.code}</div>
                        </div>

                        <div className="etbViewRow">
                            <div className="etbViewLabel">Nom:</div>
                            <div className="etbViewValue">{selectedEtablissement.nom}</div>
                        </div>

                        <div className="etbViewRow">
                            <div className="etbViewLabel">Abréviation:</div>
                            <div className="etbViewValue">{selectedEtablissement.abreviation || "—"}</div>
                        </div>

                        <div className="etbViewRow">
                            <div className="etbViewLabel">Email:</div>
                            <div className="etbViewValue">
                                <a href={`mailto:${selectedEtablissement.email}`} className="etbLink">
                                    {selectedEtablissement.email}
                                </a>
                            </div>
                        </div>

                        <div className="etbViewRow">
                            <div className="etbViewLabel">Description:</div>
                            <div className="etbViewValue">{selectedEtablissement.description || "—"}</div>
                        </div>

                        <div className="etbViewRow">
                            <div className="etbViewLabel">Logo:</div>
                            <div className="etbViewValue">
                                {selectedEtablissement.picture ? (
                                    <div className="etbViewLogoBox">
                                        <img src={getImageUrl(selectedEtablissement.picture)} alt="logo" className="etbViewLogoImg" />
                                    </div>
                                ) : (
                                    "—"
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal Suppression */}
            <Modal
                open={showDeleteModal}
                title="Confirmation de suppression"
                onClose={closeDelete}
                footer={
                    <>
                        <button className="etbBtn etbBtn--ghost" type="button" onClick={closeDelete}>
                            Annuler
                        </button>
                        <button className="etbBtn etbBtn--danger" type="button" onClick={confirmDelete}>
                            Supprimer
                        </button>
                    </>
                }
            >
                <div className="etbDeleteContent">
                    <p>
                        Voulez-vous vraiment supprimer l'établissement <strong>{selectedEtablissement?.nom}</strong> ?
                    </p>
                    <p className="etbWarning">Cette action est irréversible.</p>
                </div>
            </Modal>
        </div>
    );
}
